import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, HelpCircle } from 'lucide-react';
import type { UseMutationResult } from '@tanstack/react-query';
import type {
  PolicySettingsUpdate,
  PolicySettingsUpdateResult,
} from '@kirin/api-client/contracts/policies';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Heading1 } from '@/components/shared/typography/Heading';
import { BodyMuted } from '@/components/shared/typography/BodyText';
import { ErrorText } from '@/components/shared/typography/ErrorText';
import { useToast } from '@/hooks/use-toast';
import { createMutationHandler } from '@/hooks/mutationHelpers';
import { policySettingsToFormData, type UsePolicySettingsHook } from '../types';
import {
  CustomCapabilityRulesForm,
  type CustomCapabilityRule,
} from './CustomCapabilityRulesForm';

const EXTENSION_SECURITY_POLICY_KEY = 'extension-security';
const CUSTOM_RULES_SETTING_KEY = 'customCapabilityRules';

interface CustomCapabilityRulesPageProps {
  usePolicySettings: UsePolicySettingsHook;
  updateSettingsMutation: UseMutationResult<
    PolicySettingsUpdateResult,
    Error,
    PolicySettingsUpdate
  >;
  /** Whether the viewer may edit rules. */
  canEdit: boolean;
  /** Path back to the Policies list. */
  policiesPath: string;
}

/**
 * Canonical string for a rule: fixed key order + normalized optional `value`
 * (absent vs. `undefined` vs. `''` all collapse), so server-loaded rules and
 * locally-constructed rules never compare unequal purely due to key ordering.
 */
function canonicalizeRule(rule: CustomCapabilityRule): string {
  return JSON.stringify({
    field: rule.field ?? '',
    matchType: rule.matchType ?? '',
    value: rule.value ?? '',
    reason: rule.reason ?? '',
    severity: rule.severity ?? '',
  });
}

function areRulesEqual(
  a: CustomCapabilityRule[],
  b: CustomCapabilityRule[]
): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (rule, i) => canonicalizeRule(rule) === canonicalizeRule(b[i])
  );
}

/**
 * KIR-2691: the "Custom Capability Rules" full page
 * (Policies > Extension Security > Custom Capability Rules).
 *
 * Loads the Extension Security policy settings, edits the
 * `customCapabilityRules` array in local state, and persists it back through
 * the standard policy-settings update mutation. The built-in detection catalog
 * has its own read-only page — the two are intentionally separate.
 */
export function CustomCapabilityRulesPage({
  usePolicySettings,
  updateSettingsMutation,
  canEdit,
  policiesPath,
}: CustomCapabilityRulesPageProps): ReactElement {
  const { toast } = useToast();
  const { data, isLoading, isError, error } = usePolicySettings(
    EXTENSION_SECURITY_POLICY_KEY
  );

  const savedRules = useMemo<CustomCapabilityRule[]>(() => {
    const setting = data?.settings.find(
      s => s.key === CUSTOM_RULES_SETTING_KEY
    );
    const value = setting?.value ?? setting?.defaultValue;
    return Array.isArray(value) ? (value as CustomCapabilityRule[]) : [];
  }, [data]);

  const [rules, setRules] = useState<CustomCapabilityRule[]>(savedRules);

  // Re-seed local state whenever the persisted rules change (initial load + after save).
  useEffect(() => {
    setRules(savedRules);
  }, [savedRules]);

  const isDirty = !areRulesEqual(rules, savedRules);

  const handleSave = async (): Promise<void> => {
    if (!canEdit || !data) return;
    const settings = policySettingsToFormData(data);
    settings[CUSTOM_RULES_SETTING_KEY] = rules;

    const handler = createMutationHandler(updateSettingsMutation, toast, {
      successMessage: 'Custom capability rules saved',
      errorMessage: 'Failed to save custom capability rules',
    });
    await handler({
      policyKey: EXTENSION_SECURITY_POLICY_KEY,
      settings,
    } as PolicySettingsUpdate);
  };

  const handleDiscard = (): void => setRules(savedRules);

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Link
          to={`${policiesPath}?policy=${EXTENSION_SECURITY_POLICY_KEY}`}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="size-4" />
          Policies
        </Link>
        <div className="flex items-center gap-2">
          <Heading1>Custom Capability Rules</Heading1>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger type="button">
                <HelpCircle className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">
                  Custom rules are evaluated against extension manifests
                  independently of the built-in weighted catalog. Their severity
                  is used directly.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {isError && (
        <ErrorText as="p">
          {error instanceof Error ? error.message : 'Could not load settings.'}
        </ErrorText>
      )}

      {isLoading ? (
        <Card>
          <CardContent className="pt-6">
            <BodyMuted>Loading…</BodyMuted>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6 space-y-4">
            <CustomCapabilityRulesForm
              value={rules}
              onChange={setRules}
              disabled={!canEdit}
            />

            {canEdit && (
              <div className="flex justify-end gap-2 border-t pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDiscard}
                  disabled={!isDirty || updateSettingsMutation.isPending}
                >
                  Discard changes
                </Button>
                <Button
                  type="button"
                  onClick={handleSave}
                  disabled={!isDirty || updateSettingsMutation.isPending}
                >
                  {updateSettingsMutation.isPending
                    ? 'Saving…'
                    : 'Save changes'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
